import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ParameterConfig } from '@/types/mcpTool';
import { Brain, User, AlertCircle, Link, Search } from 'lucide-react';

interface ParameterFieldProps {
  name: string;
  value: any;
  config: ParameterConfig;
  onChange: (value: any, config: ParameterConfig) => void;
  type: 'path' | 'query';
  placeholder?: string;
}

/**
 * ParameterField Component
 *
 * Unified field for path and query parameters
 * Features:
 * - Value input
 * - 3 checkboxes: Required, Let AI Decide, Handoff to Human
 * - Works exactly like body fields
 */
export const ParameterField: React.FC<ParameterFieldProps> = ({
  name,
  value,
  config,
  onChange,
  type,
  placeholder,
}) => {
  const hasValue = value !== '' && value !== null && value !== undefined;

  const handleValueChange = (newValue: any) => {
    // When user enters a value, disable all checkboxes
    onChange(newValue, {
      ...config,
      value: newValue,
      required: false,
      let_ai_decide: false,
      handoff_to_human: false,
    });
  };

  const handleRequiredChange = (checked: boolean) => {
    onChange(value, {
      ...config,
      required: checked,
    });
  };

  const handleAIDecideChange = (checked: boolean) => {
    onChange(value, {
      ...config,
      let_ai_decide: checked,
      handoff_to_human: checked ? false : config.handoff_to_human,
    });
  };

  const handleHumanHandoffChange = (checked: boolean) => {
    onChange(value, {
      ...config,
      handoff_to_human: checked,
      let_ai_decide: checked ? false : config.let_ai_decide,
    });
  };

  const showValidationError = !hasValue && !config.let_ai_decide && !config.handoff_to_human;

  const getIcon = () => {
    return type === 'path' ? <Link className="h-4 w-4 text-blue-600" /> : <Search className="h-4 w-4 text-green-600" />;
  };

  const getBadgeColor = () => {
    return type === 'path'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-green-50 text-green-700 border-green-200';
  };

  return (
    <div className="space-y-3 p-3 rounded-lg border bg-white max-w-full overflow-hidden">
      {/* Field Header */}
      <div className="flex items-center gap-2 flex-wrap">
        {getIcon()}
        <Label htmlFor={`param-${name}`} className="flex items-center gap-2 truncate">
          {name}
        </Label>
        <Badge variant="outline" className={`text-xs ${getBadgeColor()}`}>
          {type === 'path' ? 'Path' : 'Query'}
        </Badge>
      </div>

      {/* Value Input */}
      <div className="space-y-2">
        <Input
          id={`param-${name}`}
          type="text"
          placeholder={placeholder || `Enter ${name}`}
          value={value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          className="max-w-full"
        />
      </div>

      {/* Options Checkboxes */}
      <div className="space-y-2 pt-2 border-t">
        <p className="text-xs font-medium text-gray-600">
          {hasValue ? 'Direct value entered (options disabled)' : 'Or choose an option:'}
        </p>

        <div className="space-y-2">
          {/* Required Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`required-${name}`}
              checked={config.required}
              onCheckedChange={handleRequiredChange}
              disabled={hasValue}
            />
            <Label
              htmlFor={`required-${name}`}
              className={`text-sm font-normal flex items-center gap-2 ${hasValue ? 'text-gray-400' : 'cursor-pointer'}`}
            >
              <AlertCircle className="h-4 w-4 text-red-500" />
              Required - Field must have a value
            </Label>
          </div>

          {/* Let AI Decide Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`ai-${name}`}
              checked={config.let_ai_decide}
              onCheckedChange={handleAIDecideChange}
              disabled={hasValue}
            />
            <Label
              htmlFor={`ai-${name}`}
              className={`text-sm font-normal flex items-center gap-2 ${hasValue ? 'text-gray-400' : 'cursor-pointer'}`}
            >
              <Brain className="h-4 w-4 text-purple-500" />
              Let AI Decide - AI agent will determine the value
            </Label>
          </div>

          {/* Handoff to Human Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`human-${name}`}
              checked={config.handoff_to_human}
              onCheckedChange={handleHumanHandoffChange}
              disabled={hasValue}
            />
            <Label
              htmlFor={`human-${name}`}
              className={`text-sm font-normal flex items-center gap-2 ${hasValue ? 'text-gray-400' : 'cursor-pointer'}`}
            >
              <User className="h-4 w-4 text-blue-500" />
              Handoff to Human - Human agent must provide this value
            </Label>
          </div>
        </div>

        {/* Validation Error */}
        {showValidationError && (
          <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Please either enter a value or select at least one option (Let AI Decide or Handoff to Human)
            </span>
          </div>
        )}

        {/* Selected Options Summary */}
        {!hasValue && (config.required || config.let_ai_decide || config.handoff_to_human) && (
          <div className="flex flex-wrap gap-2 pt-2">
            {config.required && (
              <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                <AlertCircle className="h-3 w-3 mr-1" />
                Required
              </Badge>
            )}
            {config.let_ai_decide && (
              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                <Brain className="h-3 w-3 mr-1" />
                AI Decides
              </Badge>
            )}
            {config.handoff_to_human && (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                <User className="h-3 w-3 mr-1" />
                Human Agent
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
